/**
 * Removes App-hidden Designer-only fields (predefined_name, is_system) from a
 * resolved FormSchema before it reaches App mode — covers both generated forms
 * (already excluded upstream via BaseFormGenerator.dataAttributes) and custom
 * forms authored in the Designer that may still bind one of these fields.
 * Immutable: returns a new schema/tree, the input is never mutated.
 */
import type { FormNode, FormSchema } from '@/designer/types/form.types'
import { isPlatformInternalAttr } from './platformInternalAttrs'

function isHiddenNode(node: FormNode): boolean {
  return isPlatformInternalAttr(node.name) || (node.dataPath != null && isPlatformInternalAttr(node.dataPath))
}

function isHiddenColumn(column: { name: string; dataPath?: string }): boolean {
  return isPlatformInternalAttr(column.name) || (column.dataPath != null && isPlatformInternalAttr(column.dataPath))
}

function stripNodes(nodes: FormNode[]): FormNode[] {
  const out: FormNode[] = []
  for (const node of nodes) {
    if (isHiddenNode(node)) continue
    let next = node
    if (node.children?.length) {
      next = { ...next, children: stripNodes(node.children) }
    }
    if (node.columns?.length) {
      next = { ...next, columns: node.columns.filter((c) => !isHiddenColumn(c)) }
    }
    out.push(next)
  }
  return out
}

export function stripAppHiddenFormFields(schema: FormSchema): FormSchema {
  return { ...schema, root: stripNodes(schema.root ?? []) }
}
